import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import PageLoader from './components/core/PageLoader';
import ScrollToTop from './components/core/ScrollToTop';
import PageTransition from './components/core/PageTransition';
import AppChooser from './components/core/AppChooser';
import usePageTitle from './hooks/usePageTitle';
import { clearKartLifeReturn, kartLifeReturn } from './components/kartlife/launchAccess';

// Cleared after the render, so StrictMode's second render still sees it.
function ReturnToKartLife({ to }) {
  useEffect(clearKartLifeReturn, []);
  return <Navigate to={to} replace />;
}

const loadAuthenticatedApp   = () => import('./AuthenticatedApp');
const AuthenticatedApp       = React.lazy(loadAuthenticatedApp);
const GamesStagingApp        = React.lazy(() => import('./components/games/GamesStagingApp'));
const AuthAction             = React.lazy(() => import('./components/auth/AuthAction'));
const Regulamin              = React.lazy(() => import('./components/pages/Regulamin'));
const PolitykaPrywatnosci    = React.lazy(() => import('./components/pages/PolitykaPrywatnosci'));
const Kontakt                = React.lazy(() => import('./components/pages/Kontakt'));
const OProjekcie             = React.lazy(() => import('./components/pages/OProjekcie'));
const DlaKartingowcow        = React.lazy(() => import('./components/pages/DlaKartingowcow'));
const XPLevelsPage           = React.lazy(() => import('./components/pages/XPLevelsPage'));
const LocationArtPreview     = React.lazy(() => import('./components/kartlife/LocationArtPreview'));
const GuestLaunch            = React.lazy(() => import('./components/kartlife/LaunchGate').then(m => ({ default: m.GuestLaunch })));

const publicPaths = new Set([
  '/',
  '/regulamin',
  '/polityka-prywatnosci',
  '/kontakt',
  '/o-projekcie',
  '/dla-kartingowcow',
  '/poziomy-xp',
  '/auth/action',
]);

export default function App() {
  const { currentUser, authLoading } = useAuth();
  const location = useLocation();
  usePageTitle();
  const isPublicPath = publicPaths.has(location.pathname);

  if (process.env.REACT_APP_LOCATION_ART_PREVIEW === 'true') {
    return <Suspense fallback={<PageLoader fullScreen />}><LocationArtPreview /></Suspense>;
  }

  if (process.env.REACT_APP_GAMES_STAGING === 'true') {
    return <Suspense fallback={<PageLoader />}><GamesStagingApp /></Suspense>;
  }

  if (currentUser) {
    if (location.pathname === '/') {
      const back = kartLifeReturn();
      if (back) return <ReturnToKartLife to={back} />;
      return <AppChooser preloadApp={loadAuthenticatedApp} />;
    }
    return (
      <Suspense fallback={<PageLoader fullScreen />}>
        <AuthenticatedApp />
      </Suspense>
    );
  }

  if (authLoading && (location.pathname === '/' || !isPublicPath)) {
    return <PageLoader fullScreen />;
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader fullScreen />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Login />} />
            <Route path="/regulamin" element={<PageTransition><Regulamin /></PageTransition>} />
            <Route path="/polityka-prywatnosci" element={<PageTransition><PolitykaPrywatnosci /></PageTransition>} />
            <Route path="/kontakt" element={<PageTransition><Kontakt /></PageTransition>} />
            <Route path="/o-projekcie" element={<PageTransition><OProjekcie /></PageTransition>} />
            <Route path="/dla-kartingowcow" element={<PageTransition><DlaKartingowcow /></PageTransition>} />
            <Route path="/poziomy-xp" element={<PageTransition><XPLevelsPage /></PageTransition>} />
            <Route path="/auth/action" element={<AuthAction />} />
            <Route path="/kartlife/*" element={<GuestLaunch />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </>
  );
}
